// ###### IMPORTS
import { useMemo } from 'react';
import routes_api from '../../../connections/routes_api';
import { createCluster, updateCluster, deleteCluster } from './notepad_services';
import useOfflineFirstData from 'services/useOfflineFirstData';
import { toast } from 'react-hot-toast';

// ###### CUSTOM HOOK
export const useNotepadClusters = () => {
  // Use offline-first hook for clusters
  const {
    data: clustersData,
    isLoading,
    error,
    refresh: refreshClusters
  } = useOfflineFirstData(routes_api.notepad_clusters);

  // Sort clusters by name ascending (alphabetical)
  const clusters = useMemo(() => {
    if (!clustersData || clustersData.length === 0) return [];
    return [...clustersData].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [clustersData]);

  // ###### FETCH CLUSTERS (kept for compatibility)
  const fetchClusters = async () => {
    await refreshClusters();
  };

  // ###### CREATE CLUSTER
  const handleCreateCluster = async (clusterData) => {
    try {
      const newCluster = await createCluster(clusterData);
      toast.success('Cluster created successfully!');
      // Refresh clusters to get updated list
      await refreshClusters();
      return newCluster;
    } catch (err) {
      toast.error('Failed to create cluster');
      throw err;
    }
  };

  // ###### UPDATE CLUSTER
  const handleUpdateCluster = async (id, clusterData) => {
    try {
      const updated = await updateCluster(id, clusterData);
      toast.success('Cluster updated successfully!');
      // Refresh clusters to get updated list
      await refreshClusters();
      return updated;
    } catch (err) {
      toast.error('Failed to update cluster');
      throw err;
    }
  };

  // ###### DELETE CLUSTER
  const handleDeleteCluster = async (id) => {
    try {
      await deleteCluster(id);
      toast.success('Cluster deleted successfully!');
      // Refresh clusters to get updated list
      await refreshClusters();
    } catch (err) {
      toast.error('Failed to delete cluster');
      throw err;
    }
  };

  return {
    clusters,
    isLoading,
    error,
    fetchClusters,
    createCluster: handleCreateCluster,
    updateCluster: handleUpdateCluster,
    deleteCluster: handleDeleteCluster
  };
};
